import { useState, useEffect, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Plus, Trash, PencilSimple, Calendar, BookOpen, 
  Buildings, PaintBrush, HardHat, Phone, Envelope, MapPin, User,
  Check, X, CaretDown, CaretUp, NoteBlank
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import OptimizedImage from "@/components/OptimizedImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GABINETE_TYPES = [
  { value: "ARQUITETOS", label: "Arquitetos", icon: Buildings, color: "bg-blue-500" },
  { value: "CONSTRUTORES", label: "Construtores", icon: HardHat, color: "bg-amber-500" },
  { value: "DESIGNERS", label: "Designers", icon: PaintBrush, color: "bg-purple-500" }
];

export default function GabineteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [gabinete, setGabinete] = useState(null);
  const [visits, setVisits] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Visit dialog state
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [visitForm, setVisitForm] = useState({ visit_date: "", comments: "" });
  const [savingVisit, setSavingVisit] = useState(false);
  const [deleteVisitId, setDeleteVisitId] = useState(null);
  
  // Delivery dialog state
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({ catalog_id: "", quantity: 1, delivery_date: "" });
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [deleteDeliveryId, setDeleteDeliveryId] = useState(null);
  const [editingDeliveryId, setEditingDeliveryId] = useState(null);
  const [editDeliveryForm, setEditDeliveryForm] = useState({ quantity: 1, delivery_date: "" });

  // Expanded visit
  const [expandedVisit, setExpandedVisit] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [gabRes, visitsRes, deliveriesRes, catalogsRes] = await Promise.all([
        axios.get(`${API}/gabinetes/${id}`),
        axios.get(`${API}/gabinetes/${id}/visits`),
        axios.get(`${API}/gabinetes/${id}/catalog-deliveries`),
        axios.get(`${API}/physical-catalogs`)
      ]);
      setGabinete(gabRes.data);
      setVisits(visitsRes.data);
      setDeliveries(deliveriesRes.data);
      setCatalogs(catalogsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
      navigate("/promocao");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Visit handlers
  const openAddVisit = () => {
    setEditingVisit(null);
    setVisitForm({ 
      visit_date: new Date().toISOString().split('T')[0], 
      comments: "" 
    });
    setShowVisitDialog(true);
  };

  const openEditVisit = (visit) => {
    setEditingVisit(visit);
    setVisitForm({
      visit_date: visit.visit_date?.split('T')[0] || "",
      comments: visit.comments || visit.notes || ""
    });
    setShowVisitDialog(true);
  };

  const handleSaveVisit = async (e) => {
    e.preventDefault();
    if (!visitForm.visit_date) {
      toast.error("Selecione a data da visita");
      return;
    }

    setSavingVisit(true);
    try {
      if (editingVisit) {
        await axios.put(`${API}/gabinetes/${id}/visits/${editingVisit.id}`, visitForm);
        toast.success("Visita atualizada");
      } else {
        await axios.post(`${API}/gabinetes/${id}/visits`, visitForm);
        toast.success("Visita registada");
      }
      setShowVisitDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao guardar visita");
    } finally {
      setSavingVisit(false);
    }
  };

  const handleDeleteVisit = async () => {
    if (!deleteVisitId) return;
    try {
      await axios.delete(`${API}/gabinetes/${id}/visits/${deleteVisitId}`);
      toast.success("Visita eliminada");
      setDeleteVisitId(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar visita");
    }
  };

  // Delivery handlers
  const openAddDelivery = () => {
    setDeliveryForm({ 
      catalog_id: catalogs[0]?.id || "", 
      quantity: 1, 
      delivery_date: new Date().toISOString().split('T')[0]
    });
    setShowDeliveryDialog(true);
  };

  const handleSaveDelivery = async (e) => {
    e.preventDefault();
    if (!deliveryForm.catalog_id) {
      toast.error("Selecione o catálogo");
      return;
    }

    setSavingDelivery(true);
    try {
      await axios.post(`${API}/gabinetes/${id}/catalog-deliveries`, deliveryForm);
      toast.success("Entrega registada");
      setShowDeliveryDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao registar entrega");
    } finally {
      setSavingDelivery(false);
    }
  };

  const handleDeleteDelivery = async () => {
    if (!deleteDeliveryId) return;
    try {
      await axios.delete(`${API}/gabinetes/${id}/catalog-deliveries/${deleteDeliveryId}`);
      toast.success("Entrega eliminada");
      setDeleteDeliveryId(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar entrega");
    }
  };

  const startEditDelivery = (delivery) => {
    setEditingDeliveryId(delivery.id);
    setEditDeliveryForm({
      quantity: delivery.quantity,
      delivery_date: delivery.delivery_date?.split('T')[0] || ""
    });
  };

  const cancelEditDelivery = () => {
    setEditingDeliveryId(null);
    setEditDeliveryForm({ quantity: 1, delivery_date: "" });
  };

  const handleSaveEditDelivery = async () => {
    if (!editingDeliveryId) return;
    try {
      await axios.put(`${API}/gabinetes/${id}/catalog-deliveries/${editingDeliveryId}`, editDeliveryForm);
      toast.success("Entrega atualizada");
      setEditingDeliveryId(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar entrega");
    }
  };

  const getTypeConfig = (type) => {
    return GABINETE_TYPES.find(t => t.value === type) || GABINETE_TYPES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (!gabinete) {
    return null;
  }

  const typeConfig = getTypeConfig(gabinete.gabinete_type);
  const TypeIcon = typeConfig.icon;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gabinete-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button 
          onClick={() => navigate(-1)} 
          className="p-2 hover:bg-stone-100 rounded-lg transition-colors mt-1"
          data-testid="btn-back"
        >
          <ArrowLeft size={20} className="text-stone-600" />
        </button>
        
        <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Logo/Initial */}
          {gabinete.logo_url ? (
            <OptimizedImage 
              src={gabinete.logo_url} 
              alt={gabinete.name}
              className="w-20 h-20 object-contain bg-white rounded-lg border border-stone-200"
              width={120}
            />
          ) : (
            <div className="w-20 h-20 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-3xl font-bold text-stone-400">
                {gabinete.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold font-[Manrope] text-stone-900">
                {gabinete.name}
              </h1>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white ${typeConfig.color}`}>
                <TypeIcon size={12} weight="bold" />
                {typeConfig.label}
              </span>
            </div>
            
            {/* Contact Info */}
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-stone-500">
              {(gabinete.contacts?.[0]?.name || gabinete.contact_person) && (
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {gabinete.contacts?.[0]?.name || gabinete.contact_person}
                  {gabinete.contacts?.[0]?.cargo && <span className="text-stone-400">· {gabinete.contacts[0].cargo}</span>}
                </span>
              )}
              {(gabinete.contacts?.[0]?.phone || gabinete.phone) && (
                <span className="flex items-center gap-1">
                  <Phone size={14} />
                  {gabinete.contacts?.[0]?.phone || gabinete.phone}
                </span>
              )}
              {gabinete.email && (
                <span className="flex items-center gap-1">
                  <Envelope size={14} />
                  {gabinete.email}
                </span>
              )}
              {gabinete.address && (
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {gabinete.address}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visits Section */}
        <div className="gresart-card">
          <div className="p-4 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
                <Calendar size={20} className="text-[#009392]" />
                Visitas
              </h2>
              <p className="text-sm text-stone-500">{visits.length} registo{visits.length !== 1 ? 's' : ''}</p>
            </div>
            <Button size="sm" className="btn-primary" onClick={openAddVisit} data-testid="btn-add-visit">
              <Plus size={16} />
              Nova
            </Button>
          </div>
          
          <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
            {visits.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                <p>Sem visitas registadas</p>
              </div>
            ) : (
              visits.map((visit) => (
                <div key={visit.id} className="border border-stone-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedVisit(expandedVisit === visit.id ? null : visit.id)}
                    className="w-full flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-stone-900">
                        {new Date(visit.visit_date).toLocaleDateString('pt-PT', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                      {(visit.comments || visit.notes) && (
                        <NoteBlank size={14} className="text-stone-400" />
                      )}
                    </div>
                    {expandedVisit === visit.id ? (
                      <CaretUp size={16} className="text-stone-400" />
                    ) : (
                      <CaretDown size={16} className="text-stone-400" />
                    )}
                  </button>
                  
                  {expandedVisit === visit.id && (
                    <div className="p-3 bg-white border-t border-stone-100">
                      {(visit.comments || visit.notes) ? (
                        <p className="text-sm text-stone-600 whitespace-pre-wrap mb-3">{visit.comments || visit.notes}</p>
                      ) : (
                        <p className="text-sm text-stone-400 italic mb-3">Sem comentários</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditVisit(visit)}
                          className="text-xs text-[#009392] hover:underline flex items-center gap-1"
                        >
                          <PencilSimple size={12} />
                          Editar
                        </button>
                        <button
                          onClick={() => setDeleteVisitId(visit.id)}
                          className="text-xs text-red-500 hover:underline flex items-center gap-1"
                        >
                          <Trash size={12} />
                          Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Catalog Deliveries Section */}
        <div className="gresart-card">
          <div className="p-4 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
                <BookOpen size={20} className="text-[#009392]" />
                Catálogos Entregues
              </h2>
              <p className="text-sm text-stone-500">
                {deliveries.reduce((sum, d) => sum + d.quantity, 0)} unidade{deliveries.reduce((sum, d) => sum + d.quantity, 0) !== 1 ? 's' : ''}
              </p>
            </div>
            <Button size="sm" className="btn-primary" onClick={openAddDelivery} data-testid="btn-add-delivery">
              <Plus size={16} />
              Nova
            </Button>
          </div>
          
          <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
            {deliveries.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <BookOpen size={32} className="mx-auto mb-2 opacity-50" />
                <p>Sem entregas registadas</p>
              </div>
            ) : (
              deliveries.map((delivery) => (
                <div 
                  key={delivery.id} 
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="px-2 py-1 bg-[#009392]/10 text-[#009392] text-xs font-medium rounded">
                      {delivery.catalog_name}
                    </span>
                    {editingDeliveryId === delivery.id ? (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-stone-500">Qtd:</span>
                          <Input
                            type="number"
                            min="1"
                            value={editDeliveryForm.quantity}
                            onChange={(e) => setEditDeliveryForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                            className="w-16 h-7 text-sm text-center"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-stone-500">Data:</span>
                          <Input
                            type="date"
                            value={editDeliveryForm.delivery_date}
                            onChange={(e) => setEditDeliveryForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                            className="w-32 h-7 text-sm"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-sm">
                          <strong className="text-[#009392]">{delivery.quantity}</strong> un.
                        </span>
                        <span className="text-xs text-stone-400">
                          {new Date(delivery.delivery_date).toLocaleDateString('pt-PT')}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {editingDeliveryId === delivery.id ? (
                      <>
                        <button
                          onClick={handleSaveEditDelivery}
                          className="p-1.5 hover:bg-green-100 text-green-600 rounded transition-colors"
                          data-testid={`btn-save-delivery-${delivery.id}`}
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={cancelEditDelivery}
                          className="p-1.5 hover:bg-stone-100 text-stone-500 rounded transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditDelivery(delivery)}
                          className="p-1.5 hover:bg-stone-100 text-stone-500 rounded transition-colors"
                          data-testid={`btn-edit-delivery-${delivery.id}`}
                        >
                          <PencilSimple size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteDeliveryId(delivery.id)}
                          className="p-1.5 hover:bg-red-100 text-stone-400 hover:text-red-500 rounded transition-colors"
                          data-testid={`btn-delete-delivery-${delivery.id}`}
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Visit Dialog */}
      <Dialog open={showVisitDialog} onOpenChange={setShowVisitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingVisit ? "Editar Visita" : "Registar Visita"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveVisit}>
            <div className="grid gap-4 py-4">
              <div>
                <Label>Data da Visita *</Label>
                <Input
                  type="date"
                  value={visitForm.visit_date}
                  onChange={(e) => setVisitForm({...visitForm, visit_date: e.target.value})}
                  className="mt-1"
                  data-testid="input-visit-date"
                />
              </div>
              <div>
                <Label>Comentários</Label>
                <Textarea
                  value={visitForm.comments}
                  onChange={(e) => setVisitForm({...visitForm, comments: e.target.value})}
                  className="mt-1"
                  rows={4}
                  placeholder="Detalhes da visita, assuntos discutidos, próximos passos..."
                  data-testid="input-visit-comments"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowVisitDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary" disabled={savingVisit}>
                {savingVisit ? "A guardar..." : (editingVisit ? "Atualizar" : "Registar")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delivery Dialog */}
      <Dialog open={showDeliveryDialog} onOpenChange={setShowDeliveryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registar Entrega de Catálogo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveDelivery}>
            <div className="grid gap-4 py-4">
              <div>
                <Label>Catálogo *</Label>
                <Select 
                  value={deliveryForm.catalog_id} 
                  onValueChange={(v) => setDeliveryForm({...deliveryForm, catalog_id: v})}
                >
                  <SelectTrigger className="mt-1" data-testid="select-catalog">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogs.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantidade</Label>
                  <Input
                    type="number"
                    min="1"
                    value={deliveryForm.quantity}
                    onChange={(e) => setDeliveryForm({...deliveryForm, quantity: parseInt(e.target.value) || 1})}
                    className="mt-1"
                    data-testid="input-quantity"
                  />
                </div>
                <div>
                  <Label>Data</Label>
                  <Input
                    type="date"
                    value={deliveryForm.delivery_date}
                    onChange={(e) => setDeliveryForm({...deliveryForm, delivery_date: e.target.value})}
                    className="mt-1"
                    data-testid="input-delivery-date"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDeliveryDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary" disabled={savingDelivery}>
                {savingDelivery ? "A guardar..." : "Registar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Visit Confirmation */}
      <AlertDialog open={!!deleteVisitId} onOpenChange={() => setDeleteVisitId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar visita?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVisit} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Delivery Confirmation */}
      <AlertDialog open={!!deleteDeliveryId} onOpenChange={() => setDeleteDeliveryId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar entrega?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDelivery} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
